import { Link } from '@tanstack/react-router'
import { CheckCircle2, ListTodo, Plus, Sparkles, X } from 'lucide-react'
import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Button, EmptyState, Spinner, toast } from '@/components/ui'
import { cn } from '@/lib/utils'

import { useTriageTaskActions, useTriageTasks } from '../queries'
import type { AssistantTask } from '../types'

const fieldClass =
  'h-9 w-full rounded-md border border-border-default bg-background-elevated px-3 text-sm text-foreground-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60'

interface TasksSectionProps {
  readonly tenantId: string
  readonly tenantSlug: string
  readonly openTasks: readonly AssistantTask[]
}

/**
 * Tarefas do assistente: abertas (sugeridas pela IA ou criadas à mão), com Concluir/Descartar, filtro de concluídas e
 * criação manual mínima. Nenhuma tarefa é executada pela IA — é uma lista para o dono.
 */
export function TasksSection({ tenantId, tenantSlug, openTasks }: TasksSectionProps) {
  const { t } = useTranslation()
  const [showDone, setShowDone] = useState(false)
  const [title, setTitle] = useState('')
  const actions = useTriageTaskActions(tenantId)
  const doneQuery = useTriageTasks(showDone ? tenantId : undefined, 'done')

  function create(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    const value = title.trim()
    if (value.length === 0 || actions.create.isPending) {
      return
    }

    actions.create.mutate(
      { title: value },
      {
        onSuccess: () => {
          setTitle('')
          toast.success(t('triage.tasks.created'))
        },
        onError: () => {
          toast.error(t('triage.tasks.actionFailed'))
        },
      },
    )
  }

  const tasks = showDone ? (doneQuery.data ?? []) : openTasks

  return (
    <section className="flex flex-col gap-3" aria-labelledby="triage-tasks-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="triage-tasks-title"
          className="inline-flex items-center gap-2 text-base font-semibold text-foreground-primary"
        >
          <ListTodo aria-hidden="true" className="h-4 w-4 text-foreground-muted" />
          {t('triage.tasks.title')}
          <Badge variant="neutral">{openTasks.length}</Badge>
        </h2>
        <div role="tablist" aria-label={t('triage.tasks.filterLabel')} className="flex gap-1">
          {(['open', 'done'] as const).map((value) => {
            const selected = (value === 'done') === showDone
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  setShowDone(value === 'done')
                }}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium',
                  selected
                    ? 'bg-background-subtle text-foreground-primary'
                    : 'text-foreground-muted hover:text-foreground-secondary',
                )}
              >
                {t(`triage.tasks.filters.${value}`)}
              </button>
            )
          })}
        </div>
      </div>

      <form onSubmit={create} className="flex flex-wrap items-center gap-2">
        <label htmlFor="triage-new-task" className="sr-only">
          {t('triage.tasks.newTaskLabel')}
        </label>
        <input
          id="triage-new-task"
          className={cn(fieldClass, 'min-w-0 flex-1 sm:max-w-md')}
          placeholder={t('triage.tasks.newTaskPlaceholder')}
          value={title}
          maxLength={200}
          onChange={(event) => {
            setTitle(event.target.value)
          }}
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          loading={actions.create.isPending}
          disabled={title.trim().length === 0}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          {t('triage.tasks.add')}
        </Button>
      </form>

      {showDone && doneQuery.isPending ? (
        <Spinner label={t('loading')} />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title={showDone ? t('triage.tasks.emptyDoneTitle') : t('triage.tasks.emptyTitle')}
          description={
            showDone ? t('triage.tasks.emptyDoneDescription') : t('triage.tasks.emptyDescription')
          }
          className="py-8"
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-default rounded-lg border border-border-default bg-background-elevated">
          {tasks.map((task) => (
            <li
              key={task.id}
              className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span className="inline-flex flex-wrap items-center gap-2 text-sm font-medium text-foreground-primary">
                  {task.title}
                  {task.origin === 'agent' ? (
                    <Badge variant="info">
                      <Sparkles aria-hidden="true" className="mr-1 h-3 w-3" />
                      {t('triage.tasks.suggestedByAi')}
                    </Badge>
                  ) : null}
                  {task.dueHint !== null ? <Badge variant="neutral">{task.dueHint}</Badge> : null}
                </span>
                {task.details !== null ? (
                  <span className="text-sm text-foreground-secondary">{task.details}</span>
                ) : null}
                {task.conversationSeq !== null ? (
                  <Link
                    to="/app/$tenantSlug/desk"
                    params={{ tenantSlug }}
                    search={{ conversation: task.conversationSeq }}
                    className="text-xs text-foreground-muted underline-offset-2 hover:underline"
                  >
                    {t('triage.tasks.fromConversation', {
                      contact: task.contactName ?? t('desk.list.unknownContact'),
                    })}
                  </Link>
                ) : null}
              </div>
              {task.status === 'open' ? (
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={actions.complete.isPending && actions.complete.variables === task.id}
                    onClick={() => {
                      actions.complete.mutate(task.id, {
                        onError: () => {
                          toast.error(t('triage.tasks.actionFailed'))
                        },
                      })
                    }}
                  >
                    <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                    {t('triage.tasks.complete')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={actions.dismiss.isPending && actions.dismiss.variables === task.id}
                    onClick={() => {
                      actions.dismiss.mutate(task.id, {
                        onError: () => {
                          toast.error(t('triage.tasks.actionFailed'))
                        },
                      })
                    }}
                  >
                    <X aria-hidden="true" className="h-4 w-4" />
                    {t('triage.tasks.dismissTask')}
                  </Button>
                </div>
              ) : (
                <Badge variant="success">{t('triage.tasks.done')}</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
